/**
 * Consistent letter case for street addresses and city names from listing feeds, which
 * arrive as "403 W DIVISION ST", "2939 Market ST. Ne" or "1080 SW 12th ave".
 * Follows postal conventions: directionals and route prefixes in capitals (NE, US 27),
 * ordinals lowercase (82nd), every other word capitalized (Rd, Ste, Saint Johns).
 * Text already written in mixed case keeps its deliberate capitals (McLoughlin, GAR Hwy).
 */

const wordSet = (words: string) => new Set(words.trim().split(/\s+/));

// Always capitals, whatever case they arrive in.
const UPPER_WORDS = wordSet('N S E W NE NW SE SW US U.S SR CR FM RR PO P.O');

// Street types and unit words: an all-caps one inside mixed-case text is a shouted word,
// not an acronym ("Market ST." → "Market St.").
const STREET_WORDS = wordSet(`
  ST STREET AVE AV AVENUE RD ROAD DR DRIVE BLVD BOULEVARD HWY HIGHWAY LN LANE CT COURT
  PL PLACE PKWY PKY PARKWAY TPKE PIKE CIR CIRCLE WAY TER TRL SQ EXPY FWY BYP LOOP PLZ
  PLAZA CTR XING HTS CSWY EXT RT RTE ROUTE STE SUITE UNIT APT BLDG BUILDING
`);

// A state code names a road when a number or one of these follows ("NJ 36", "IL HIGHWAY 1").
const ROAD_WORDS = wordSet('HIGHWAY HWY ROUTE RTE RT');

// CT is left out: it is far more often Court than Connecticut.
const STATE_CODES = wordSet(`
  AL AK AZ AR CA CO DC DE FL GA HI ID IL IN IA KS KY LA ME MD MA MI MN MS MO MT NE NV NH
  NJ NM NY NC ND OH OK OR PA RI SC SD TN TX UT VT VA WA WV WI WY
`);

const SMALL_WORDS = wordSet('OF THE AND AT');

// Longest all-caps word in mixed-case text still read as an acronym (GAR Hwy, DIX Hwy);
// longer ones are shouted words ("1050 PITTSBURGH St").
const MAX_ACRONYM_LENGTH = 3;

const capitalize = (part: string) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase();

function titleWord(word: string): string {
  return word
    .split('-')
    .map(part => {
      const lower = capitalize(part);
      if (/^Mc[a-z]{2,}/.test(lower)) return `Mc${capitalize(lower.slice(2))}`;
      // O'Neil, D'Angelo; but Driver's, not Driver'S
      if (/^[A-Za-z]'[a-z]{2,}/.test(lower)) {
        return `${lower.slice(0, 2)}${capitalize(lower.slice(2))}`;
      }
      return lower;
    })
    .join('-');
}

/** Tokens whose case is fixed by what they are (NE, 82nd, 41A, B, NJ 36); else undefined. */
function codeCase(core: string, next: string | undefined): string | undefined {
  const upper = core.toUpperCase();
  if (UPPER_WORDS.has(upper) || /^[A-Za-z]$/.test(core)) return upper;
  if (/\d/.test(core)) {
    const ordinal = /^(\d+)(st|nd|rd|th)$/i.exec(core);
    return ordinal ? `${ordinal[1]}${ordinal[2]?.toLowerCase()}` : upper;
  }
  const namesRoad = next !== undefined && (/^\d/.test(next) || ROAD_WORDS.has(next.toUpperCase()));
  return STATE_CODES.has(upper) && namesRoad ? upper : undefined;
}

function wordCase(core: string, isFirst: boolean, convert: boolean): string {
  const upper = core.toUpperCase();
  const isUpper = core === upper;
  if (!isUpper && core !== core.toLowerCase()) {
    // Deliberate mixed case stays (McLoughlin), except a lowercased Mc (Mcloughlin).
    return /^Mc[a-z]{2,}$/.test(core) ? titleWord(core) : core;
  }
  const isAcronym = core.length <= MAX_ACRONYM_LENGTH && !STREET_WORDS.has(upper);
  if (isUpper && !convert && isAcronym) return core;
  if (!isFirst && SMALL_WORDS.has(upper)) return core.toLowerCase();
  return titleWord(core);
}

export function formatAddressCase(value: string): string {
  const text = value.trim().replace(/\s+/g, ' ');
  if (!text) return text;
  // All-caps or all-lowercase text is converted in full; mixed-case text only where needed.
  const convert = !/[a-z]/.test(text) || !/[A-Z]/.test(text);
  const words = text.split(' ');

  return words
    .map((word, index) => {
      const [, lead = '', core = '', trail = ''] =
        /^([^A-Za-z0-9]*)(.*?)([^A-Za-z0-9]*)$/.exec(word) ?? [];
      if (!core) return word;
      const cased = codeCase(core, words[index + 1]) ?? wordCase(core, index === 0, convert);
      return lead + cased + trail;
    })
    .join(' ');
}
